/**
 * /workspace/credits — AI credit balance, burn analytics, top-up packs.
 */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Zap, TrendingDown, TrendingUp, Sparkles, Loader2, IndianRupee, Flame,
  Search, ExternalLink, CheckCircle2, XCircle, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

interface Balance {
  balance: number;
  total_credit: number;
  total_debit: number;
  by_kind: Array<{ kind: string; spent: number }>;
  burn_14d: Array<{ day: string; spent: number }>;
  packs: Array<{ id: string; label: string; credits: number; price_inr: number; bonus?: number }>;
}
interface Entry {
  entry_id: string; kind: string; credit: number; debit: number;
  note?: string; pack_label?: string; price_inr?: number;
  created_at: string;
}

const KIND_LABEL: Record<string, string> = {
  sibascore_generation: "SibaScore",
  nano_banana_image:    "Nano Banana image",
  claude_reply:         "Claude first-reply",
  blog_generation:      "AI Blog article",
  compare_verdict:      "Compare verdict",
  signal_classification:"Signal classify",
  brand_studio_asset:   "Brand Studio",
  topup:                "Top-up",
};

export default function CreditsPage() {
  const [kind, setKind] = React.useState("all");
  const qc = useQueryClient();

  const bal = useQuery<Balance>({
    queryKey: ["credits-balance"],
    queryFn: async () => (await api.get("/workspace/credits/balance")).data,
    refetchInterval: 30_000,
  });
  const led = useQuery<{ items: Entry[] }>({
    queryKey: ["credits-ledger", kind],
    queryFn: async () => (await api.get(`/workspace/credits/ledger?limit=100&kind=${kind}`)).data,
  });

  const topup = useMutation({
    mutationFn: async (pack_id: string) => (await api.post("/workspace/credits/topup", { pack_id })).data,
    onSuccess: (d) => {
      toast.success(`+${d?.credits_added?.toLocaleString?.() || d?.credits_added} credits added 🎉`);
      qc.invalidateQueries({ queryKey: ["credits-balance"] });
      qc.invalidateQueries({ queryKey: ["credits-ledger"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || "Top-up failed"),
  });

  return (
    <DashboardLayout title="AI Credits" subtitle="Every AI action burns credits — track balance, spend and top up in one place.">
      {/* Balance hero */}
      <div className="rounded-2xl overflow-hidden relative border border-[color:var(--sg-border)] mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-[color:var(--sb-navy,#0A1128)] via-fuchsia-800 to-[color:var(--sb-saffron,#F97316)]"/>
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 20% 40%, rgba(249,115,22,0.35), transparent 40%), radial-gradient(circle at 80% 60%, rgba(217,70,239,0.3), transparent 40%)" }}/>
        <div className="relative px-6 py-6 sm:px-8 sm:py-7 text-white flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-white/70 flex items-center gap-1.5"><Zap className="h-3 w-3" strokeWidth={2.5}/> AI credit balance</div>
            <div className="mt-2 font-serif-display text-5xl sm:text-6xl font-bold leading-tight tabular-nums" data-testid="credits-balance">
              {bal.data?.balance?.toLocaleString?.() ?? "—"}
            </div>
            <div className="mt-1 text-sm text-white/70">
              +{bal.data?.total_credit?.toLocaleString?.() ?? "—"} added · −{bal.data?.total_debit?.toLocaleString?.() ?? "—"} spent
            </div>
          </div>
        </div>
      </div>

      {/* Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4 mb-6">
        <BurnChart data={bal.data?.burn_14d || []}/>
        <SpendPie by_kind={bal.data?.by_kind || []}/>
      </div>

      {/* Top-up packs */}
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] mb-3 flex items-center gap-1.5"><Sparkles className="h-3 w-3"/> Top up</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(bal.data?.packs || []).map((p) => {
            const busy = topup.isPending && topup.variables === p.id;
            return (
              <div key={p.id} data-testid={`credits-pack-${p.id}`} className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-4 flex flex-col">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--sb-saffron,#F97316)]">{p.label}</div>
                <div className="font-serif-display text-3xl font-bold text-[color:var(--sg-fg)] tabular-nums mt-1">{p.credits.toLocaleString()}</div>
                {p.bonus ? (
                  <div className="text-[11px] text-emerald-700 dark:text-emerald-300 font-semibold">+ {p.bonus.toLocaleString()} bonus</div>
                ) : null}
                <div className="mt-2 text-sm text-[color:var(--sg-muted)]"><IndianRupee className="h-3 w-3 inline"/> {p.price_inr.toLocaleString()}</div>
                <Button
                  data-testid={`credits-buy-${p.id}`}
                  onClick={() => topup.mutate(p.id)}
                  disabled={busy}
                  className="mt-3 h-9 bg-[color:var(--sb-saffron,#F97316)] hover:bg-[color:var(--sb-saffron-hover,#EA580C)] text-white font-semibold"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Buy pack"}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Ledger */}
      <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--sg-border)]">
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)]">Recent activity</div>
          <select
            data-testid="credits-kind-filter"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="h-8 text-xs rounded-md border border-[color:var(--sg-border)] bg-transparent px-2"
          >
            <option value="all">All kinds</option>
            {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {led.isLoading ? (
          <div className="p-8 text-center text-[color:var(--sg-muted)]"><Loader2 className="h-4 w-4 inline animate-spin mr-2"/> Loading…</div>
        ) : (led.data?.items?.length ?? 0) === 0 ? (
          <div className="p-10 text-center text-[color:var(--sg-muted)]">No entries.</div>
        ) : (
          <div className="overflow-x-auto sg-scrollbar-thin">
            <table className="w-full text-sm" data-testid="credits-ledger">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] border-b border-[color:var(--sg-border)]">
                  <th className="text-left py-2 pl-4">When</th>
                  <th className="text-left py-2">Kind</th>
                  <th className="text-left py-2">Note</th>
                  <th className="text-right py-2 pr-4">Credit / Debit</th>
                </tr>
              </thead>
              <tbody>
                {led.data!.items.map((e) => (
                  <tr key={e.entry_id} className="border-b border-[color:var(--sg-border)]/60">
                    <td className="py-2 pl-4 text-[color:var(--sg-muted)]">{new Date(e.created_at).toLocaleString()}</td>
                    <td className="py-2"><span className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sg-fg)]">{KIND_LABEL[e.kind] || e.kind}</span></td>
                    <td className="py-2 text-[color:var(--sg-fg)]">{e.note}</td>
                    <td className="py-2 pr-4 text-right tabular-nums font-semibold">
                      {e.credit > 0 && <span className="text-emerald-600 dark:text-emerald-300">+{e.credit.toLocaleString()}</span>}
                      {e.debit > 0 && <span className="text-rose-600 dark:text-rose-300">−{e.debit.toLocaleString()}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* AI Cart Search History — every URL ingest this user has run, with per-row credit cost */}
      <div className="mt-6">
        <AICartSearchHistoryCard />
      </div>
    </DashboardLayout>
  );
}

// ---------------------------------------------------------------------------
// AI Cart Search History — credit-cost + audit trail card
// ---------------------------------------------------------------------------
interface SearchHistoryRow {
  url: string;
  marketplace: string;
  title?: string | null;
  price_amount?: number | null;
  price_currency?: string | null;
  source?: string | null;
  cache_hit: boolean;
  success: boolean;
  error?: string | null;
  has_socibaba_match?: boolean;
  comparisons_count?: number;
  created_at: string;
  credit_cost: number;
}
interface SearchHistoryPayload {
  rows: SearchHistoryRow[];
  credit_cost_per_fresh_search: number;
  credits_month: number;
  credits_all_time: number;
  searches_month: number;
  searches_all_time: number;
  month_started_at: string;
  as_of: string;
}

function AICartSearchHistoryCard() {
  const qc = useQueryClient();
  const q = useQuery<SearchHistoryPayload>({
    queryKey: ["ai-cart-search-history"],
    queryFn: async () => (await api.get("/ingest/history?limit=100")).data,
    refetchInterval: 60_000,
  });

  const monthLabel = q.data?.month_started_at
    ? new Date(q.data.month_started_at).toLocaleString(undefined, { month: "long", year: "numeric" })
    : "This month";

  return (
    <div
      data-testid="ai-cart-search-history"
      className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--sg-border)] gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] flex items-center gap-1.5">
            <Search className="h-3 w-3" /> AI Cart™ Search History
          </div>
          <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5">
            Every URL you've ingested. Cache hits & failed fetches are free — only fresh scrapes burn credits.
          </div>
        </div>
        <button
          data-testid="ai-cart-search-refresh"
          onClick={() => qc.invalidateQueries({ queryKey: ["ai-cart-search-history"] })}
          className="inline-flex items-center gap-1 rounded-md border border-[color:var(--sg-border)] px-2 py-1 text-[11px] font-medium hover:bg-[color:var(--sg-muted-bg)]"
          title="Refresh"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[color:var(--sg-border)] border-b border-[color:var(--sg-border)]">
        <StatCell
          label={`Credits burned · ${monthLabel}`}
          value={q.data?.credits_month?.toLocaleString?.() ?? "—"}
          hint={`${q.data?.searches_month ?? 0} searches`}
          accent="text-rose-600 dark:text-rose-300"
          testid="ai-cart-search-credits-month"
        />
        <StatCell
          label="Credits burned · all-time"
          value={q.data?.credits_all_time?.toLocaleString?.() ?? "—"}
          hint={`${q.data?.searches_all_time ?? 0} searches`}
          accent="text-[color:var(--sg-fg)]"
          testid="ai-cart-search-credits-all"
        />
        <StatCell
          label="Cost per fresh scrape"
          value={`${q.data?.credit_cost_per_fresh_search ?? 1} credit`}
          hint="Cache hits & fails = 0"
          accent="text-[color:var(--sb-saffron,#F97316)]"
          testid="ai-cart-search-cost-per"
        />
        <StatCell
          label="Free look-ups this month"
          value={(
            (q.data?.searches_month ?? 0) -
            (q.data?.credits_month ?? 0) / Math.max(1, q.data?.credit_cost_per_fresh_search ?? 1)
          ).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          hint="Cache hits + failed fetches"
          accent="text-emerald-600 dark:text-emerald-300"
          testid="ai-cart-search-free"
        />
      </div>

      {/* Table */}
      {q.isLoading ? (
        <div className="p-8 text-center text-[color:var(--sg-muted)]">
          <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Loading history…
        </div>
      ) : (q.data?.rows?.length ?? 0) === 0 ? (
        <div className="p-10 text-center text-[color:var(--sg-muted)] text-sm">
          No AI Cart™ searches yet. Head to <a href="/ai-cart" className="underline">/ai-cart</a> and paste a URL to get started.
        </div>
      ) : (
        <div className="overflow-x-auto sg-scrollbar-thin">
          <table className="w-full text-sm" data-testid="ai-cart-search-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] border-b border-[color:var(--sg-border)]">
                <th className="text-left py-2 pl-4">When</th>
                <th className="text-left py-2">Marketplace</th>
                <th className="text-left py-2">Product</th>
                <th className="text-left py-2">Status</th>
                <th className="text-right py-2 pr-4">Credits</th>
              </tr>
            </thead>
            <tbody>
              {q.data!.rows.map((r, i) => (
                <tr key={`${r.url}-${r.created_at}-${i}`} className="border-b border-[color:var(--sg-border)]/60">
                  <td className="py-2 pl-4 text-[color:var(--sg-muted)] whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="py-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sg-fg)]">
                      {r.marketplace || "other"}
                    </span>
                  </td>
                  <td className="py-2 text-[color:var(--sg-fg)] max-w-[380px]">
                    <div className="truncate flex items-center gap-1.5">
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="truncate hover:underline"
                        title={r.url}
                      >
                        {r.title || r.url}
                      </a>
                      <ExternalLink className="h-3 w-3 shrink-0 text-[color:var(--sg-muted)]" />
                    </div>
                    {r.price_amount ? (
                      <div className="text-[11px] text-[color:var(--sg-muted)] tabular-nums">
                        {r.price_currency || "INR"} {r.price_amount.toLocaleString()}
                        {r.has_socibaba_match && (
                          <span className="ml-2 inline-block rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 px-1.5 text-[10px] font-bold uppercase tracking-widest">
                            SociBaba match
                          </span>
                        )}
                      </div>
                    ) : null}
                  </td>
                  <td className="py-2">
                    {r.success && !r.cache_hit && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
                        <CheckCircle2 className="h-3 w-3" /> Fresh scrape
                      </span>
                    )}
                    {r.success && r.cache_hit && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 dark:text-sky-300">
                        <Sparkles className="h-3 w-3" /> Cache hit
                      </span>
                    )}
                    {!r.success && (
                      <span
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 dark:text-rose-300"
                        title={r.error || undefined}
                      >
                        <XCircle className="h-3 w-3" /> {r.error || "failed"}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums font-semibold">
                    {r.credit_cost > 0 ? (
                      <span className="text-rose-600 dark:text-rose-300">−{r.credit_cost}</span>
                    ) : (
                      <span className="text-[color:var(--sg-muted)]">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCell({ label, value, hint, accent, testid }: {
  label: string; value: string; hint?: string; accent?: string; testid?: string;
}) {
  return (
    <div className="p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[color:var(--sg-muted)]">{label}</div>
      <div className={`mt-1 font-serif-display text-2xl font-bold tabular-nums ${accent || "text-[color:var(--sg-fg)]"}`}>
        {value}
      </div>
      {hint && <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5">{hint}</div>}
    </div>
  );
}

function BurnChart({ data }: { data: Array<{ day: string; spent: number }> }) {
  const max = Math.max(1, ...data.map((d) => d.spent));
  const total = data.reduce((s, d) => s + d.spent, 0);
  return (
    <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5" data-testid="credits-burn">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] flex items-center gap-1.5"><Flame className="h-3 w-3"/> 14-day burn</div>
        <span className="text-[10px] text-[color:var(--sg-muted)]">{total.toLocaleString()} credits</span>
      </div>
      {data.length === 0 ? (
        <div className="text-sm text-[color:var(--sg-muted)] py-4">No burn yet.</div>
      ) : (
        <div className="flex items-end justify-between gap-1 h-28">
          {data.map((d) => (
            <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: ${d.spent}`}>
              <div className="w-full flex-1 flex items-end">
                <div className="w-full rounded-t-md bg-gradient-to-t from-rose-500 to-[color:var(--sb-saffron,#F97316)] shadow-lg" style={{ height: `${Math.max(4, (d.spent / max) * 100)}%`, transition: "height 500ms" }}/>
              </div>
              <div className="text-[9px] font-mono text-[color:var(--sg-muted)]">{d.day.slice(5)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SpendPie({ by_kind }: { by_kind: Array<{ kind: string; spent: number }> }) {
  const total = Math.max(1, by_kind.reduce((s, x) => s + x.spent, 0));
  return (
    <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5" data-testid="credits-by-kind">
      <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] flex items-center gap-1.5 mb-3"><TrendingDown className="h-3 w-3"/> Spend by kind</div>
      {by_kind.length === 0 ? (
        <div className="text-sm text-[color:var(--sg-muted)] py-4">No spend yet.</div>
      ) : (
        <div className="space-y-2">
          {by_kind.map((r) => {
            const pct = (r.spent / total) * 100;
            return (
              <div key={r.kind}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-[color:var(--sg-fg)]">{KIND_LABEL[r.kind] || r.kind}</span>
                  <span className="text-[color:var(--sg-muted)] tabular-nums">{r.spent.toLocaleString()} · {pct.toFixed(0)}%</span>
                </div>
                <div className="mt-1 h-2 rounded-full bg-[color:var(--sg-muted-bg)] overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[color:var(--sb-saffron,#F97316)] to-fuchsia-500" style={{ width: `${pct}%` }}/>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
